import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import axios from 'axios';
import { injectable } from 'inversify';

export type HttpResponse<T = unknown, D = unknown> = AxiosResponse<T, D>;
export type HttpRequestConfig<D = unknown> = AxiosRequestConfig<D>;

export interface IHttpInterceptor<V> {
  onFulfilled?: (value: V) => Promise<V> | V;
  onRejected?: (error: unknown) => unknown;
}

export interface IHttpService {
  get: <T = unknown, R = HttpResponse<T>, D = unknown>(url: string, config?: HttpRequestConfig<D>) => Promise<R>;

  post: <T = unknown, R = HttpResponse<T>, D = unknown>(
    url: string,
    data?: D,
    config?: HttpRequestConfig<D>
  ) => Promise<R>;

  put: <D = unknown>(url: string, data?: D, config?: HttpRequestConfig<D>) => Promise<void>;

  addResponseInterceptor: (interceptor: IHttpInterceptor<HttpResponse>) => void;
}

@injectable()
export class HttpService implements IHttpService {
  private _instance: AxiosInstance;

  public constructor() {
    const config = {
      baseURL: import.meta.env.BASE_URL + 'api/',
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      }
    };
    this._instance = axios.create(config);
  }

  public addResponseInterceptor(interceptor: IHttpInterceptor<HttpResponse>): void {
    this._instance.interceptors.response.use(interceptor.onFulfilled, interceptor.onRejected);
  }

  public post<T = unknown, R = HttpResponse<T, unknown>, D = unknown>(
    url: string,
    data?: D,
    config?: HttpRequestConfig<D>
  ): Promise<R> {
    return this._instance.post(url, data, config);
  }

  public get<T = unknown, R = HttpResponse<T>, D = unknown>(url: string, config?: HttpRequestConfig<D>): Promise<R> {
    return this._instance.get(url, config);
  }

  public put<D = unknown>(url: string, data?: D, config?: HttpRequestConfig<D>): Promise<void> {
    return this._instance.put(url, data, config);
  }
}
